import React, { useRef } from 'react';
import { Paperclip, X, FileText } from 'lucide-react';
import { toast } from 'sonner';

// Mirrors the server limits (services/attachments.py). The SERVER is
// authoritative — this is only to fail fast and be friendly in the UI.
export const MAX_FILES = 10;
export const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,application/pdf';
const OK_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf'];

const prettySize = (n) =>
  n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`;

// Filter candidates against type/size/count, warning on anything dropped.
// Returns the accepted File[].
export const filterFiles = (incoming, existing) => {
  const out = [];
  let count = existing.length;
  for (const f of incoming) {
    if (count >= MAX_FILES) { toast.error(`Up to ${MAX_FILES} files per message.`); break; }
    if (!OK_TYPES.includes(f.type)) { toast.error(`${f.name || 'That file'} isn't an image or PDF.`); continue; }
    if (f.size > MAX_BYTES) { toast.error(`${f.name || 'That file'} is over 10 MB.`); continue; }
    out.push(f);
    count += 1;
  }
  return out;
};

// Pull file(s) out of a clipboard paste (e.g. a pasted screenshot). Returns File[].
export const filesFromPaste = (e) => {
  const items = (e.clipboardData && e.clipboardData.items) || [];
  const files = [];
  for (const it of items) {
    if (it.kind === 'file') {
      const f = it.getAsFile();
      if (f) files.push(f);
    }
  }
  return files;
};

// The compose-side control: an "attach" button + chips for the queued files.
const AttachmentPicker = ({ files, setFiles, disabled }) => {
  const inputRef = useRef(null);

  const add = (incoming) => {
    const accepted = filterFiles(Array.from(incoming || []), files);
    if (accepted.length) setFiles([...files, ...accepted]);
  };
  const onPick = (e) => { add(e.target.files); e.target.value = ''; };
  const remove = (idx) => setFiles(files.filter((_, i) => i !== idx));

  return (
    <div className="mt-2">
      <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden onChange={onPick} />
      <button
        type="button"
        onClick={() => inputRef.current && inputRef.current.click()}
        disabled={disabled || files.length >= MAX_FILES}
        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-border text-muted-foreground hover:bg-secondary/40 transition disabled:opacity-50"
      >
        <Paperclip className="w-3.5 h-3.5" /> Attach image or PDF
      </button>
      {files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {files.map((f, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-secondary/50 border border-border"
            >
              <FileText className="w-3 h-3 text-muted-foreground" />
              <span className="truncate max-w-[160px]">{f.name || 'pasted-image.png'}</span>
              <span className="text-muted-foreground">{prettySize(f.size)}</span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Remove attachment"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default AttachmentPicker;
